import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, X, Zap, Smartphone, Calendar, Bell, Share, Plus } from 'lucide-react';

let isAnyPwaPromptMounted = false;

function isPostUpdateSettling(): boolean {
  try {
    const appliedAt = sessionStorage.getItem('acs_update_applied_at');
    if (!appliedAt) return false;
    const elapsed = Date.now() - parseInt(appliedAt, 10);
    if (elapsed < 20000) {
      return true;
    }
    sessionStorage.removeItem('acs_update_applied_at');
  } catch (_) {}
  return false;
}

export function PwaPrompts() {
  const [isDuplicate] = useState(() => {
    if (isAnyPwaPromptMounted) {
      console.log('[PWA] Duplicate PwaPrompts rendering suppressed synchronously');
      return true;
    }
    isAnyPwaPromptMounted = true;
    return false;
  });

  const [installPromptEvent, setInstallPromptEvent] = useState<any>(null);
  const [showUpdate, setShowUpdate] = useState(false);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showIosPrompt, setShowIosPrompt] = useState(false);

  useEffect(() => {
    if (!showUpdate) return;
    // Never auto-refresh during an active arena game — the forced reload would
    // drop a player from their match. Only show the banner and let them choose.
    const isInArenaGame = /\/arena\/[^/]+/.test(window.location.pathname);
    if (isInArenaGame) {
      console.log('[PWA] Update available but suppressing auto-refresh during active arena game.');
      return;
    }
    const timer = window.setTimeout(() => {
      console.log('[PWA] Auto-refreshing because a new frontend version is available.');
      void handleUpdateClick();
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [showUpdate]);

  useEffect(() => {
    if (isDuplicate) return;

    let settleTimer: number | undefined;
    if (isPostUpdateSettling()) {
      console.log('[PWA] Post-update settle window active; suppressing duplicate update prompt');
      settleTimer = window.setTimeout(() => {
        try {
          sessionStorage.removeItem('acs_update_applied_at');
        } catch (_) {}
      }, 20000);
    }

    // Check if user dismissed the install prompt recently (within last 3 days)
    const dismissedTime = localStorage.getItem('acs_pwa_install_dismissed');
    let dismissed = false;
    if (dismissedTime) {
      const threeDays = 3 * 24 * 60 * 60 * 1000;
      if (Date.now() - parseInt(dismissedTime, 10) < threeDays) {
        setIsDismissed(true);
        dismissed = true;
      }
    }

    // Recover early captured beforeinstallprompt event if available
    if ((window as any).deferredInstallPrompt) {
      console.log('[PWA] Recovering early deferredInstallPrompt event');
      setInstallPromptEvent((window as any).deferredInstallPrompt);
    }

    // iOS prompt detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;
    if (isIOS && !isStandalone && !dismissed) {
      console.log('[PWA] iOS browser detected, showing manual A2HS prompt');
      setShowIosPrompt(true);
    }

    // 1. Listen for the Install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('[PWA] beforeinstallprompt event fired');
      e.preventDefault(); // Prevent the mini-infobar from appearing on mobile
      setInstallPromptEvent(e);
      (window as any).deferredInstallPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for custom early prompt available event
    const handleEarlyPromptAvailable = () => {
      if ((window as any).deferredInstallPrompt) {
        console.log('[PWA] beforeinstallprompt event resolved via early capture');
        setInstallPromptEvent((window as any).deferredInstallPrompt);
      }
    };
    window.addEventListener('pwa-install-available', handleEarlyPromptAvailable);

    // Custom window event listener for foreground app update messages
    const handleAppUpdateReady = () => {
      if (isPostUpdateSettling()) return;
      console.log('[PWA] Received app-update-ready window event');
      setShowUpdate(true);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          const reg = regs.find(r => r.active?.scriptURL.includes('firebase-messaging-sw.js') || r.installing?.scriptURL.includes('firebase-messaging-sw.js') || r.waiting?.scriptURL.includes('firebase-messaging-sw.js'));
          if (reg) {
            console.log('[PWA] Triggering SW update check due to foreground signal');
            reg.update().catch(err => console.warn('[PWA] Triggered update check failed:', err));
          }
        });
      }
    };
    window.addEventListener('app-update-ready', handleAppUpdateReady);

    // 2. Register Service Worker and listen for updates
    const isDevEnvironment = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      Boolean((import.meta as any).env?.DEV)
    );

    if (isDevEnvironment) {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister().catch(() => {}));
        });
      }
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name).catch(() => {}));
        });
      }
      return;
    }

    if ('serviceWorker' in navigator) {
      const handleSWMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'APP_UPDATE') {
          if (isPostUpdateSettling()) return;
          console.log('[PWA] Received APP_UPDATE signal from Service Worker');
          setShowUpdate(true);
          navigator.serviceWorker.getRegistrations().then(regs => {
            const reg = regs.find(r => r.active?.scriptURL.includes('firebase-messaging-sw.js') || r.installing?.scriptURL.includes('firebase-messaging-sw.js') || r.waiting?.scriptURL.includes('firebase-messaging-sw.js'));
            if (reg) {
              console.log('[PWA] Triggering SW update check due to SW message signal');
              reg.update().catch(err => console.warn('[PWA] Triggered update check failed:', err));
            }
          });
        }
      };
      navigator.serviceWorker.addEventListener('message', handleSWMessage);

      const registerSW = async () => {
        try {
          const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          setSwRegistration(reg);

          // Check if there's already a waiting worker on load
          if (reg.waiting && !isPostUpdateSettling()) {
            console.log('[PWA] Waiting worker found immediately on register');
            setShowUpdate(true);
          }

          // Check if there's an installing worker on load and track it
          if (reg.installing && !isPostUpdateSettling()) {
            console.log('[PWA] Installing worker found immediately on register');
            const worker = reg.installing;
            const onStateChange = () => {
              if (worker.state === 'installed' && navigator.serviceWorker.controller && !isPostUpdateSettling()) {
                console.log('[PWA] Installing worker completed installation, showing update');
                setShowUpdate(true);
                worker.removeEventListener('statechange', onStateChange);
              }
            };
            worker.addEventListener('statechange', onStateChange);
          }

          // Listen for new updates while the app is open
          reg.addEventListener('updatefound', () => {
            const newWorker = reg.installing;
            if (newWorker) {
              console.log('[PWA] New worker installing...');
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller && !isPostUpdateSettling()) {
                  // New update available and installed
                  console.log('[PWA] New worker installed and ready, showing update');
                  setShowUpdate(true);
                }
              });
            }
          });

          // Force check for updates immediately on page load
          console.log('[PWA] Triggering initial update check on mount...');
          reg.update().catch(err => console.warn('[PWA] Initial update check failed:', err));

          // Periodic check for updates (every hour)
          const updateInterval = setInterval(() => {
            console.log('[PWA] Periodic update check...');
            reg.update().catch(() => {});
          }, 60 * 60 * 1000);

          // Check for updates when the app becomes visible again (skip during active arena games)
          const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
              const isInArenaGame = /\/arena\/[^/]+/.test(window.location.pathname);
              if (isInArenaGame) return;
              console.log('[PWA] Tab visible, checking for updates...');
              reg.update().catch(() => {});
            }
          };
          document.addEventListener('visibilitychange', handleVisibilityChange);

          return () => {
            clearInterval(updateInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
          };
        } catch (err) {
          console.log('[PWA] SW registration failed: ', err);
        }
      };

      const cleanupPromise = registerSW();
      
      // Handle the case where the SW is waiting to activate on reload
      navigator.serviceWorker.ready.then(reg => {
        if (reg.waiting && !isPostUpdateSettling()) {
          setShowUpdate(true);
        }
      });

      return () => {
        isAnyPwaPromptMounted = false;
        if (settleTimer) window.clearTimeout(settleTimer);
        navigator.serviceWorker?.removeEventListener?.('message', handleSWMessage);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('pwa-install-available', handleEarlyPromptAvailable);
        window.removeEventListener('app-update-ready', handleAppUpdateReady);
        cleanupPromise.then(cleanup => cleanup && cleanup());
      };
    }

    return () => {
      isAnyPwaPromptMounted = false;
      if (settleTimer) window.clearTimeout(settleTimer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-install-available', handleEarlyPromptAvailable);
      window.removeEventListener('app-update-ready', handleAppUpdateReady);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const { outcome } = await installPromptEvent.userChoice;
    if (outcome === 'accepted') {
      setInstallPromptEvent(null);
    }
  };

  const handleDismissInstall = () => {
    localStorage.setItem('acs_pwa_install_dismissed', Date.now().toString());
    setIsDismissed(true);
    setShowIosPrompt(false);
  };

  const handleUpdateClick = async () => {
    console.log('[PWA] Refresh button clicked');
    try {
      sessionStorage.setItem('acs_update_applied_at', Date.now().toString());
    } catch (_) {}
    
    // Add the listener first so we don't miss the event!
    let reloadFired = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!reloadFired) {
        reloadFired = true;
        console.log('[PWA] Controller changed, reloading...');
        window.location.reload();
      }
    });

    try {
      let reg = swRegistration;
      if (!reg) {
        const regs = await navigator.serviceWorker.getRegistrations();
        reg = regs.find(r => r.active?.scriptURL.includes('firebase-messaging-sw.js') || r.installing?.scriptURL.includes('firebase-messaging-sw.js') || r.waiting?.scriptURL.includes('firebase-messaging-sw.js')) || null;
      }
      if (reg) {
        // Trigger update check first if neither is present
        if (!reg.waiting && !reg.installing) {
          console.log('[PWA] Manual update check initiated on refresh click...');
          await reg.update();
        }

        if (reg.waiting) {
          console.log('[PWA] Sending SKIP_WAITING to waiting worker');
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Fallback reload if controllerchange doesn't fire in 1.5 seconds
          setTimeout(() => {
            if (!reloadFired) {
              reloadFired = true;
              console.log('[PWA] Fallback reload triggered');
              window.location.reload();
            }
          }, 1500);
        } else if (reg.installing) {
          console.log('[PWA] Service Worker is installing. Waiting for installation...');
          const worker = reg.installing;
          
          const onStateChange = () => {
            if (worker.state === 'installed') {
              console.log('[PWA] Service Worker installed, sending SKIP_WAITING');
              worker.postMessage({ type: 'SKIP_WAITING' });
              worker.removeEventListener('statechange', onStateChange);
            }
          };
          worker.addEventListener('statechange', onStateChange);

          // Fallback reload if worker installation stalls
          setTimeout(() => {
            if (!reloadFired) {
              reloadFired = true;
              console.log('[PWA] Fallback reload triggered (installing timeout)');
              window.location.reload();
            }
          }, 4000);
        } else {
          console.log('[PWA] No waiting or installing worker found after check. Reloading...');
          window.location.reload();
        }
      } else {
        console.log('[PWA] No SW registration found. Force reloading...');
        window.location.reload();
      }
    } catch (err) {
      console.error('[PWA] Error during update click:', err);
      window.location.reload();
    }
  };

  if (isDuplicate) {
    return null;
  }

  return (
    <>
      {/* Slick Update Toast Notification */}
      {showUpdate && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] w-full max-w-[420px] px-4 pointer-events-none">
          <div className="bg-slate-900/95 backdrop-blur-xl border border-blue-500/40 p-6 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col gap-6 pointer-events-auto animate-reveal border-t-blue-400/50 relative">
            <button
              type="button"
              onClick={() => setShowUpdate(false)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl transition-colors cursor-pointer bg-transparent border-none"
              aria-label="Dismiss"
            >
              <X size={18} />
            </button>
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 p-4 bg-blue-600/20 text-blue-400 rounded-2xl shadow-inner border border-blue-500/20 mt-1">
                <RefreshCw size={28} className="animate-spin" />
              </div>
              <div className="flex-1 pr-6">
                <h3 className="text-xl font-black text-white leading-tight tracking-tight uppercase">
                  New Version <span className="text-blue-400">Ready</span>
                </h3>
                <p className="text-base text-slate-300 mt-2 leading-relaxed font-medium">
                  We've pushed some critical updates to the Lair. Refresh now to stay sharp.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button 
                type="button"
                onClick={handleUpdateClick}
                className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 text-white text-sm font-black rounded-2xl transition-all active:scale-[0.97] cursor-pointer shadow-lg shadow-blue-900/40 border-none uppercase tracking-widest flex items-center justify-center gap-2"
              >
                <RefreshCw size={16} />
                Refresh Now
              </button>
              <button
                type="button"
                onClick={() => setShowUpdate(false)}
                className="px-4 py-4 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-bold rounded-2xl transition-all active:scale-[0.97] cursor-pointer border border-white/10"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Install App Modal (A2HS) */}
      {installPromptEvent && !showUpdate && !isDismissed && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md z-[100] p-4">
          <div className="bg-slate-900/90 border border-white/10 p-6 md:p-8 rounded-3xl shadow-2xl w-full max-w-md animate-reveal flex flex-col gap-6 text-white relative">
            
            {/* Close Button */}
            <button 
              onClick={handleDismissInstall}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="flex flex-col items-center text-center gap-3 mt-2">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/15 shadow-lg shadow-red-900/20">
                <img src="/apple-touch-icon.png" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white">Install ATL Card Sharks</h3>
                <p className="text-xs text-slate-400 mt-1 max-w-[280px] mx-auto leading-relaxed">
                  Add the app to your home screen for instant access and the ultimate premium experience.
                </p>
              </div>
            </div>

            {/* Benefits List */}
            <div className="flex flex-col gap-4 py-2">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-600/10 text-red-500 rounded-lg mt-0.5">
                  <Smartphone size={16} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider">Full-Screen App</h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">Launches instantly in full-screen mode without browser tabs or clutter.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 bg-red-600/10 text-red-500 rounded-lg mt-0.5">
                  <Bell size={16} />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider">Real-Time Alerts</h4>
                  <p className="text-[11px] text-slate-400 mt-0.5">Stay connected with instant push alerts for match updates and RSVPs.</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 mt-2">
              <button 
                onClick={handleInstallClick}
                className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-red-900/20 active:scale-98 transition-all cursor-pointer border-none flex items-center justify-center gap-2"
              >
                <Download size={14} />
                Install App
              </button>
              <button 
                onClick={handleDismissInstall}
                className="w-full py-3 text-slate-400 hover:text-white text-xs font-bold transition-colors cursor-pointer bg-transparent border-none"
              >
                Maybe Later
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Custom iOS Install App Modal (A2HS) */}
      {showIosPrompt && !showUpdate && !isDismissed && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md z-[100] p-4 animate-fadeIn">
          <div className="bg-slate-900/90 border border-white/10 p-6 md:p-8 rounded-3xl shadow-2xl w-full max-w-md animate-reveal flex flex-col gap-6 text-white relative">
            
            {/* Close Button */}
            <button 
              onClick={handleDismissInstall}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl transition-colors cursor-pointer bg-transparent border-none"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="flex flex-col items-center text-center gap-3 mt-2">
              <div className="w-16 h-16 rounded-2xl overflow-hidden border border-white/15 shadow-lg shadow-red-900/20">
                <img src="/apple-touch-icon.png" alt="Logo" className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white">Add to Home Screen</h3>
                <p className="text-xs text-slate-400 mt-1 max-w-[280px] mx-auto leading-relaxed">
                  Install ATL Card Sharks on your iPhone to access rankings, events, and real-time alerts.
                </p>
              </div>
            </div>

            {/* iOS Instructions */}
            <div className="flex flex-col gap-4 py-2 border-y border-white/5 my-1">
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center text-xs font-black flex-shrink-0">
                  1
                </div>
                <div className="text-slate-300 text-xs font-medium leading-relaxed flex-1">
                  Tap the <span className="text-white font-bold inline-flex items-center gap-1 bg-white/10 px-1.5 py-0.5 rounded-md">Share <Share size={12} className="text-blue-400" /></span> button in Safari.
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center text-xs font-black flex-shrink-0">
                  2
                </div>
                <div className="text-slate-300 text-xs font-medium leading-relaxed flex-1">
                  Scroll down and tap <span className="text-white font-bold inline-flex items-center gap-1 bg-white/10 px-1.5 py-0.5 rounded-md">Add to Home Screen <Plus size={12} className="text-red-500" /></span>.
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center text-xs font-black flex-shrink-0">
                  3
                </div>
                <div className="text-slate-300 text-xs font-medium leading-relaxed flex-1">
                  Tap <span className="text-white font-bold bg-white/10 px-1.5 py-0.5 rounded-md">Add</span> in the top-right corner.
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 mt-2">
              <button 
                onClick={handleDismissInstall}
                className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-500 hover:to-red-700 text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-red-900/20 active:scale-98 transition-all cursor-pointer border-none flex items-center justify-center"
              >
                Got It
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}